import { useState, useRef } from "react";
import { Input } from "@/components/ui/input";

type CategoryNameInputProps = {
  value: string;
  onChange: (v: string) => void;
  /** Called when a suggestion is selected (clicked or Enter on highlighted item). */
  onSelectSuggestion?: (name: string) => void;
  /** Platform-level default category names to show as suggestions. */
  suggestions?: string[];
  /** Already-created store category names — hidden from the suggestions dropdown. */
  existingNames?: string[];
  placeholder?: string;
  maxLength?: number;
  /** Extra className applied to the outer wrapping div (e.g. flex sizing). */
  className?: string;
  /** className applied to the inner Input element. */
  inputClassName?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  "aria-invalid"?: boolean;
};

export function CategoryNameInput({
  value,
  onChange,
  onSelectSuggestion,
  suggestions = [],
  existingNames = [],
  placeholder,
  maxLength,
  className,
  inputClassName,
  disabled,
  autoFocus,
  onKeyDown,
  "aria-invalid": ariaInvalid,
}: CategoryNameInputProps) {
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [open, setOpen] = useState(false);
  const suppressBlurRef = useRef(false);

  const existingLower = new Set(existingNames.map((n) => n.toLowerCase()));
  const filtered =
    value.trim().length > 0
      ? suggestions.filter(
          (s) =>
            s.toLowerCase().includes(value.toLowerCase()) && !existingLower.has(s.toLowerCase())
        )
      : [];

  const showDropdown = open && filtered.length > 0;

  function selectSuggestion(name: string) {
    onChange(name);
    onSelectSuggestion?.(name);
    setOpen(false);
    setHighlightedIndex(-1);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (showDropdown) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightedIndex((i) => Math.min(i + 1, filtered.length - 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightedIndex((i) => Math.max(i - 1, -1));
        return;
      }
      if (e.key === "Enter" && highlightedIndex >= 0) {
        e.preventDefault();
        selectSuggestion(filtered[highlightedIndex]);
        return;
      }
      if (e.key === "Escape") {
        setOpen(false);
        setHighlightedIndex(-1);
      }
    }
    onKeyDown?.(e);
  }

  return (
    <div className={className}>
      <div className="relative">
        <Input
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setOpen(true);
            setHighlightedIndex(-1);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            if (suppressBlurRef.current) {
              suppressBlurRef.current = false;
              return;
            }
            setOpen(false);
            setHighlightedIndex(-1);
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          maxLength={maxLength}
          disabled={disabled}
          autoFocus={autoFocus}
          aria-invalid={ariaInvalid}
          aria-autocomplete="list"
          aria-expanded={showDropdown}
          className={inputClassName}
        />
        {showDropdown && (
          <ul
            role="listbox"
            className="absolute z-50 left-0 right-0 top-full mt-1 max-h-48 overflow-y-auto rounded-md border border-border bg-popover shadow-md py-1"
          >
            {filtered.map((name, i) => (
              <li
                key={name}
                role="option"
                aria-selected={i === highlightedIndex}
                onMouseDown={() => {
                  suppressBlurRef.current = true;
                  selectSuggestion(name);
                }}
                className={`px-3 py-1.5 text-sm cursor-pointer ${
                  i === highlightedIndex
                    ? "bg-accent text-accent-foreground"
                    : "hover:bg-accent hover:text-accent-foreground"
                }`}
              >
                {name}
              </li>
            ))}
          </ul>
        )}
      </div>
      {typeof maxLength === "number" && (
        <p
          className={`text-xs text-right ${
            value.length > maxLength ? "text-destructive" : "text-muted-foreground"
          }`}
        >
          {value.length}/{maxLength}
        </p>
      )}
    </div>
  );
}
